import React, { useEffect, useState } from "react";
import { UserData } from "./UserData";
import ListView from "../../component/ListView/ListView";
import { showToast } from "../../utils/Toastify/Toastify";
import { userDelete } from "../../api/HttpApis";
import { useLocation, useNavigate } from "react-router-dom";
import UserInvite from "../userInvite/UserInvite";
import { useSearch } from "../../context/SearchContext";
import { useRbac } from "../../rbac/RbacContext";

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

// Filter dropdown options for the Users list.
const USER_TYPE_OPTIONS = [
  { label: "Premium", value: "Premium" },
  { label: "Standard", value: "Standard" },
];
// Matches the exact, case-sensitive Subscription.platform values the backend
// filter compares against (see SubscribersController.list()) — options here
// that don't exist in the data (old "Apple"/"Manual") never matched anything.
const PLATFORM_OPTIONS = [
  { label: "Stripe", value: "stripe" },
  { label: "Web", value: "web" },
  { label: "Google Play", value: "google_play" },
  { label: "Roku", value: "roku" },
  { label: "Fire TV", value: "fire_tv" },
  { label: "Amazon", value: "amazon" },
  { label: "Free", value: "free" },
  { label: "Admin-granted", value: "admin" },
];
// Labelled by what the filter actually measures — whether the address was
// confirmed — not by signup progress: legacy, admin-created and bulk-imported
// accounts are never sent a code, so they read as "Email not verified" despite
// being complete signups. That is also why this defaults to "All" (no value)
// rather than hiding unverified accounts, which would hide those too.
const SIGNUP_STATE_OPTIONS = [
  { label: "Email verified", value: "verified" },
  { label: "Email not verified", value: "pending" },
];
const DATE_OPTIONS = [
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
  { label: "Last year", value: "year" },
];

const Users = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { searchQuery } = useSearch();
  const { can } = useRbac();
  const canCreateUser = can("users", "create");
  const [user, setUser] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [handleEditId, setHandleEditId] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    user_type: "",
    platform: "",
    date: "",
    signup_state: "",
  });

  const fetchData = async (page, limit) => {
    try {
      const { data, totalCount } = await UserData(
        page,
        limit,
        searchQuery,
        filters
      );
      setUser(data || []);
      setTotalCount(totalCount);
    } catch (error) {
      const response = error?.response?.data;
      if (response?.errors) {
        const fieldErrors = Object.values(response.errors).flat();
        if (fieldErrors.length > 0) {
          showToast(fieldErrors[0], "error");
        } else if (response.message) {
          showToast(response.message, "error");
        }
      } else if (response?.message) {
        showToast(response.message, "error");
      }
    }
  };

  useEffect(() => {
    if (location.state?.openModal) {
      setIsModalOpen(true);
      setHandleEditId(null);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery === "" || searchQuery) {
        fetchData();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

 const handleView = (id) => {
  navigate(`/mainlayout/users/${id}`);
};

  const handleEdit = (id) => {
    setEditingId(id);
    setTimeout(() => {
      setHandleEditId(id);
      setIsModalOpen(true);
      setEditingId(null);
    }, 500);
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this user?"
    );
    if (!confirmed) return;

    setDeletingId(id);
    try {
      const response = await userDelete(id);
      setUser((prev) => prev.filter((item) => item.id !== id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      showToast(response.data.message, "success");
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to delete user.",
        "error"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const cardFor = (item) => ({
    id: item.id,
    thumbLabel: initials(item.name),
    title: item.name,
    meta: [item.email, item.updatedDate || "—"].filter(Boolean).join(" · "),
    badge: item.user ? { label: item.user, tone: "neutral" } : null,
    badges: [
      item.emailVerified === false
        ? { label: "Email not verified", tone: "warning" }
        : null,
    ],
    // Clicking the row opens the detail page, same as the eye/View action.
    // (The action buttons stopPropagation, so they won't double-trigger this.)
    onClick: () => handleView(item.id),
    actions: [
      {
        icon: "view",
        label: "View",
        onClick: () => handleView(item.id),
      },
      {
        icon: "edit",
        label: "Edit",
        disabled: editingId === item.id,
        onClick: () => handleEdit(item.id),
      },
      {
        icon: "delete",
        label: "Delete",
        disabled: deletingId === item.id,
        onClick: () => handleDelete(item.id),
      },
    ],
  });

  const stats = [
    { label: "Total Users", value: (totalCount || 0).toLocaleString() },
  ];

  return (
    <div>
      <ListView
        heading="Users"
        rowsOnly
        data={user}
        getCard={cardFor}
        stats={stats}
        totalCount={totalCount}
        onPageChange={fetchData}
        customFilters={[
          {
            key: "user_type",
            placeholder: "User Type",
            value: filters.user_type,
            options: USER_TYPE_OPTIONS,
            onChange: (v) => handleFilterChange("user_type", v),
          },
          {
            key: "platform",
            placeholder: "Platform",
            value: filters.platform,
            options: PLATFORM_OPTIONS,
            onChange: (v) => handleFilterChange("platform", v),
          },
          {
            key: "signup_state",
            placeholder: "All users",
            value: filters.signup_state,
            options: SIGNUP_STATE_OPTIONS,
            onChange: (v) => handleFilterChange("signup_state", v),
          },
          {
            key: "date",
            placeholder: "Date Added",
            value: filters.date,
            options: DATE_OPTIONS,
            onChange: (v) => handleFilterChange("date", v),
          },
        ]}
        addLabel="Add User"
        onAdd={
          canCreateUser
            ? () => {
                setHandleEditId(null);
                setIsModalOpen(true);
              }
            : undefined
        }
      />
      <UserInvite
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onInviteSuccess={fetchData}
        handleEditId={handleEditId}
        setHandleEditId={setHandleEditId}
      />
    </div>
  );
};

export default Users;